import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { ITerminalUser, ITerminalUserParams } from '../../models/user.model';

export interface IUsersService {
  addUser(
    user: ITerminalUser,
    delayInvite?: boolean,
  ): Observable<ITerminalUser>;
  updateUser(id: string, user: ITerminalUser): Observable<ITerminalUser>;
  resendUserInvite(
    id: string,
    delayInvite?: boolean,
  ): Observable<ITerminalUser>;
  deactivateUser(id: string): Observable<ITerminalUser>;
}

export interface IUpdateUser {
  user: ITerminalUserParams;
  invite_after_onboarding?: boolean;
}

@Injectable()
export class UsersService implements IUsersService {
  readonly authBaseUrl = environment.apiAuthUri;

  constructor(private http: HttpClient) {}

  addUser(
    user: ITerminalUser,
    delayInvite?: boolean,
  ): Observable<ITerminalUser> {
    const url = `${this.authBaseUrl}/api/terminal/users`;
    return this.http.post<ITerminalUser>(
      url,
      this.getParams(user, delayInvite),
    );
  }

  updateUser(id: string, user: ITerminalUser): Observable<ITerminalUser> {
    const url = `${this.authBaseUrl}/api/terminal/users/${id}`;
    return this.http.patch<ITerminalUser>(url, this.getParams(user));
  }

  deactivateUser(id: string): Observable<ITerminalUser> {
    const url = `${this.authBaseUrl}/api/terminal/users/${id}/deactivate`;
    return this.http.post<ITerminalUser>(url, {});
  }

  resendUserInvite(
    id: string,
    delayInvite?: boolean,
  ): Observable<ITerminalUser> {
    const url = `${this.authBaseUrl}/api/terminal/users/${id}/resend_invite`;
    return this.http.post<ITerminalUser>(url, {
      invite_after_onboarding: delayInvite,
    });
  }

  /**
   * Convert an ITerminalUser to params to update that user
   */
  private getParams(user: ITerminalUser, delayInvite?: boolean): IUpdateUser {
    const params: IUpdateUser = { user: {} };

    if (!!user.first_name) {
      params.user.first_name = user.first_name;
    }

    if (!!user.last_name) {
      params.user.last_name = user.last_name;
    }

    if (!!user.email) {
      params.user.email = user.email;
    }

    if (!!user.phone) {
      params.user.phone = user.phone;
    }

    params.user.npn = user.npn ?? '';

    if (delayInvite) {
      params.invite_after_onboarding = true;
    }

    this.addGroupMembershipsToParams(params.user, user);

    return params;
  }

  private addGroupMembershipsToParams(
    params: ITerminalUserParams,
    user: ITerminalUser,
  ) {
    // If the user already has group memberships, then we just want to modify the primary one
    // to be whatever the current primary_user_group is
    params.user_group_memberships_attributes = user.user_group_memberships.map(
      (group) => ({
        id: group.id,
        user_group_id: group.is_primary
          ? user.primary_user_group.id
          : group.user_group_id,
      }),
    );

    // If they don't have any group memberships, then we want to add the one that is currently the
    // primary
    if (params.user_group_memberships_attributes.length === 0) {
      params.user_group_memberships_attributes = [
        { user_group_id: user.primary_user_group.id },
      ];
    }
  }
}
